/**
 * Store section behaviour.
 *
 * A page template cannot apply Search & Discovery facets, so every filter,
 * artist and sort change is resolved by fetching the collection's
 * `?view=tjupi-store` fragment — where Shopify does apply them — and swapping
 * the grid in. The visible URL keeps the page's own path so links stay shareable.
 */
(function () {
  'use strict';

  var TYPE_PARAM = 'filter.p.product_type';

  function init(root) {
    if (root.dataset.tjupiStoreReady) return;
    root.dataset.tjupiStoreReady = 'true';

    var body = root.querySelector('[data-tjupi-store-body]');
    var collectionUrl = root.dataset.collectionUrl;
    var fragmentView = root.dataset.fragmentView;
    var artistParam = root.dataset.artistParam;
    if (!body || !collectionUrl) return;

    var pending = null;

    function currentParams() {
      return new URLSearchParams(window.location.search);
    }

    function render(params, options) {
      var fragmentParams = new URLSearchParams(params);
      fragmentParams.set('view', fragmentView);

      root.classList.add('is-loading');

      if (pending) pending.abort();
      pending = new AbortController();

      return fetch(collectionUrl + '?' + fragmentParams.toString(), {
        signal: pending.signal
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Store fragment request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          body.innerHTML = html;
          syncUI(params);
          root.classList.remove('is-loading');
          if (options && options.scroll) {
            root.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        })
        .catch(function (error) {
          if (error.name === 'AbortError') return;
          console.error(error);
          root.classList.remove('is-loading');
        });
    }

    function apply(params, options) {
      var query = params.toString();
      window.history.pushState(
        {},
        '',
        window.location.pathname + (query ? '?' + query : '')
      );
      return render(params, options);
    }

    function syncUI(params) {
      var activeType = params.get(TYPE_PARAM) || '';
      root.querySelectorAll('[data-tjupi-type]').forEach(function (link) {
        link.classList.toggle('is-active', link.dataset.tjupiType === activeType);
      });

      var sort = root.querySelector('[data-tjupi-sort]');
      if (sort) sort.value = params.get('sort_by') || sort.options[0].value;

      var activeArtist = params.get(artistParam);
      var input = root.querySelector('[data-tjupi-artist-input]');
      if (input && !activeArtist) input.value = '';
      if (input && activeArtist) {
        var match = root.querySelector('[data-tjupi-artist-value="' + activeArtist + '"]');
        if (match) input.value = match.textContent.replace(/\s*\(\d+\)\s*$/, '').trim();
      }
    }

    root.addEventListener('click', function (event) {
      var typeLink = event.target.closest('[data-tjupi-type]');
      if (typeLink) {
        event.preventDefault();
        var params = currentParams();
        params.delete('page');
        if (typeLink.dataset.tjupiType) {
          params.set(TYPE_PARAM, typeLink.dataset.tjupiType);
        } else {
          params.delete(TYPE_PARAM);
        }
        apply(params);
        return;
      }

      var artistButton = event.target.closest('[data-tjupi-artist-value]');
      if (artistButton) {
        event.preventDefault();
        var artistParams = currentParams();
        artistParams.delete('page');
        artistParams.set(artistParam, artistButton.dataset.tjupiArtistValue);
        closeArtistList();
        apply(artistParams);
        return;
      }

      // Paginate inside the fragment rather than navigating to the collection.
      var pageLink = event.target.closest('.tjupi-pagination a');
      if (pageLink) {
        event.preventDefault();
        var linkParams = new URLSearchParams(pageLink.search);
        var nextParams = currentParams();
        var nextPage = linkParams.get('page');
        if (nextPage) {
          nextParams.set('page', nextPage);
        } else {
          nextParams.delete('page');
        }
        apply(nextParams, { scroll: true });
      }
    });

    var sortSelect = root.querySelector('[data-tjupi-sort]');
    if (sortSelect) {
      sortSelect.addEventListener('change', function () {
        var params = currentParams();
        params.delete('page');
        params.set('sort_by', sortSelect.value);
        apply(params);
      });
    }

    var artistSearch = root.querySelector('[data-tjupi-artist-search]');
    var artistInput = root.querySelector('[data-tjupi-artist-input]');
    var artistList = root.querySelector('[data-tjupi-artist-list]');

    function closeArtistList() {
      if (artistList) artistList.hidden = true;
    }

    if (artistInput && artistList) {
      var options = Array.prototype.map.call(
        artistList.querySelectorAll('li'),
        function (item) {
          return {
            item: item,
            label: item.textContent.replace(/\s*\(\d+\)\s*$/, '').trim().toLowerCase()
          };
        }
      );

      artistInput.addEventListener('input', function () {
        var query = artistInput.value.trim().toLowerCase();
        var matches = 0;
        options.forEach(function (option) {
          var show = query === '' || option.label.indexOf(query) !== -1;
          option.item.hidden = !show;
          if (show) matches += 1;
        });
        artistList.hidden = matches === 0;

        // Clearing the field drops the artist facet.
        if (query === '' && currentParams().get(artistParam)) {
          var params = currentParams();
          params.delete(artistParam);
          params.delete('page');
          apply(params);
        }
      });

      artistInput.addEventListener('focus', function () {
        artistList.hidden = false;
      });

      document.addEventListener('click', function (event) {
        if (!artistSearch.contains(event.target)) closeArtistList();
      });
    }

    window.addEventListener('popstate', function () {
      render(currentParams());
    });

    // A shared or reloaded URL arrives with facets the page template ignored.
    if (/[?&](filter\.|sort_by=|page=)/.test(window.location.search)) {
      render(currentParams());
    } else {
      syncUI(currentParams());
      root.classList.remove('is-loading');
    }
  }

  function initAll() {
    document.querySelectorAll('[data-tjupi-store]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', initAll);
})();
